// src/pages/Chat/NewDMModal.jsx
//
// ─── ENDPOINT NOTE ────────────────────────────────────────────────────────────
// Update SEARCH_URL to match your employee list endpoint.
// Check src/api/employeeService.js for the exact URL your app uses.
// Common values:
//   "/accounts/api/users/"
//   "/employees/api/employees/"
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiX, FiSearch, FiLoader } from "react-icons/fi";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import apiClient from "../../api/apiClient";
import { createDirectConversation, getKeyBundle } from "../../api/chatService";
import useChatStore from "../../store/chatStore";
import { ChatCrypto } from "../../utils/crypto";
import useAuthStore from "../../store/authStore";

const SEARCH_URL = "/accounts/api/users/";   // ← update if different

function getInitials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

function UserAvatar({ photo, name }) {
  const [imgErr, setImgErr] = useState(false);
  const resolvedPhoto = resolvePhotoUrl(photo);

  if (resolvedPhoto && !imgErr) {
    return (
      <img
        src={resolvedPhoto}
        alt={name}
        className="w-full h-full object-cover"
        onError={() => setImgErr(true)}
      />
    );
  }
  return getInitials(name);
}

/** Normalise any API user shape into a consistent object */
function normaliseUser(u) {
  const firstName = u.first_name || "";
  const lastName  = u.last_name  || "";
  const fullName  =
    u.full_name ||
    (firstName || lastName ? `${firstName} ${lastName}`.trim() : "") ||
    u.username ||
    u.employee_id ||
    "Unknown";

  return {
    employee_id: u.employee_id ? String(u.employee_id) : (u.id ? String(u.id) : ""),
    full_name:   fullName,
    role:        u.role        || u.designation || "",
    department:  u.department  || u.dept        || "",
    photo:       u.photo       || u.profile_photo || u.avatar || null,
  };
}

/** Client-side filter — handles backends that don't support ?search= */
function clientFilter(users, query, currentUserId) {
  const q = query.toLowerCase().trim();
  if (!q) return users;
  return users.filter((u) => {
    const isYou = u.employee_id === currentUserId;
    return (
      u.full_name.toLowerCase().includes(q) ||
      u.employee_id.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q) ||
      u.department.toLowerCase().includes(q) ||
      (isYou && "you".includes(q))
    );
  });
}

export default function NewDMModal({ onClose, currentUserId }) {
  const [query,     setQuery]     = useState("");
  const [allUsers,  setAllUsers]  = useState([]);   // full list fetched once
  const [results,   setResults]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [creating,  setCreating]  = useState(null);
  const [error,     setError]     = useState(null);

  const inputRef           = useRef(null);
  const hasFetchedRef      = useRef(false);
  const upsertConversation = useChatStore((s) => s.upsertConversation);
  const selectConversation = useChatStore((s) => s.selectConversation);

  // Auto-focus
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Fetch the full employee list once on mount
  const fetchAll = useCallback(async () => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get(SEARCH_URL, { params: { all_for_chat: "true" } });
      const raw = Array.isArray(data)
        ? data
        : data.users || data.results || data.employees || data.data || [];

      const normalised = raw
        .map(normaliseUser)
        .filter((u) => u.employee_id);

      // Ensure current user is in the list so they can start a "SELF" chat
      const hasSelf = normalised.some(u => String(u.employee_id) === String(currentUserId));
      if (!hasSelf && currentUserId) {
        try {
          const user = useAuthStore.getState().user || {};
          const profilePhoto = useAuthStore.getState().profilePhoto;
          normalised.unshift({
            employee_id: currentUserId,
            full_name:   user.full_name || "You",
            role:        user.role || "",
            department:  user.department || user.dept || "",
            photo:       profilePhoto || null,
          });
        } catch (e) {
          console.warn("Could not add self to user list", e);
        }
      }

      setAllUsers(normalised);
      setResults(normalised);   // show all initially
    } catch (err) {
      console.error("NewDMModal fetch error:", err);
      setError(
        `Could not load employees. ` +
        `Check SEARCH_URL in NewDMModal.jsx (currently: "${SEARCH_URL}"). ` +
        `Error: ${err?.response?.status || err.message}`
      );
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // Trigger fetch when user opens the input (types first char)
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Trigger filter when query or allUsers changes
  useEffect(() => {
    setResults(clientFilter(allUsers, query, currentUserId));
  }, [query, allUsers, currentUserId]);

  const handleSelect = async (user) => {
    if (!user.employee_id) {
      setError("This user has no valid employee ID.");
      return;
    }
    if (creating) return;
    setCreating(user.employee_id);
    setError(null);
    try {
      // 1. Fetch recipient's public key
      let encryptedKeys = null;
      try {
        const { data: bundle } = await getKeyBundle(user.employee_id);
        if (bundle && bundle.identity_public_key) {
          console.log("[Crypto] Found recipient public key. Initializing E2EE...");
          
          // Generate a random conversation key (32 bytes)
          const convKey = window.crypto.getRandomValues(new Uint8Array(32));
          const convKeyBase64 = btoa(String.fromCharCode(...convKey));
          
          // Import keys for ECDH
          const myKeyPair = await ChatCrypto.getIdentityKeyPair();
          const theirPubKey = await ChatCrypto.importPublicKey(bundle.identity_public_key);
          
          // Derive shared secret with unique salts for each member
          const generateMemberKey = async (targetEid, targetPubKey, bundleVersion = 1) => {
            const salt = window.crypto.getRandomValues(new Uint8Array(16));
            const saltB64 = ChatCrypto.arrayBufferToBase64(salt);
            const shared = await ChatCrypto.deriveSharedKey(myKeyPair.privateKey, targetPubKey, saltB64);
            const enc = await ChatCrypto.encrypt(convKeyBase64, shared);
            return {
              member_employee_id: targetEid,
              encrypted_key:      enc.ciphertext,
              key_iv:             enc.iv,
              hkdf_salt:          saltB64,
              key_bundle_version: bundleVersion
            };
          };

          const isSelfKey = String(user.employee_id) === String(currentUserId);
          if (isSelfKey) {
            encryptedKeys = [
              await generateMemberKey(currentUserId, myKeyPair.publicKey, 1)
            ];
          } else {
            encryptedKeys = [
              await generateMemberKey(currentUserId, myKeyPair.publicKey, 1),
              await generateMemberKey(user.employee_id, theirPubKey, bundle.rotation_count || 1)
            ];
          }
        }
      } catch (keyErr) {
        console.warn("[Crypto] Could not fetch recipient keys. Falling back to plaintext.", keyErr);
      }

      // 2. Create conversation
      const isSelf = String(user.employee_id) === String(currentUserId);
      const { data } = await createDirectConversation(
        user.employee_id, 
        encryptedKeys, 
        isSelf ? "SELF" : "DIRECT"
      );
      upsertConversation(data);
      selectConversation(data.id);
      onClose();
    } catch (err) {
      console.error("[NewDMModal] handleSelect error:", err);
      let msg = "Could not start conversation. Try again.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d.error) msg = d.error;
        else if (d.detail) msg = d.detail;
        else if (typeof d === "object") {
          // Flatten DRF errors if any
          const firstKey = Object.keys(d)[0];
          const firstErr = Array.isArray(d[firstKey]) ? d[firstKey][0] : d[firstKey];
          msg = `${firstKey}: ${firstErr}`;
        }
      }
      setError(msg);
    } finally {
      setCreating(null);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed z-[71] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm px-4">
        <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <span className="text-sm font-semibold text-slate-100">New Message</span>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/10 transition-colors"
            >
              <FiX size={15} />
            </button>
          </div>

          {/* Search */}
          <div className="px-4 py-3 border-b border-white/8">
            <div className="relative">
              <FiSearch size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, ID, role…"
                className="
                  w-full pl-8 pr-8 py-2 rounded-lg text-sm
                  bg-white/5 border border-white/10 text-slate-200
                  placeholder-slate-600 outline-none
                  focus:border-blue-500/50 transition-colors
                "
              />
              {loading && (
                <FiLoader size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 animate-spin" />
              )}
            </div>
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>

            {/* Loading state */}
            {loading && allUsers.length === 0 && (
              <div className="flex flex-col items-center py-8 gap-2">
                <div className="w-5 h-5 border-2 border-blue-500/40 border-t-blue-500 rounded-full animate-spin" />
                <span className="text-xs text-slate-500">Loading employees…</span>
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="text-xs text-red-400 px-4 py-4 text-center">{error}</p>
            )}

            {/* No results */}
            {!loading && !error && results.length === 0 && allUsers.length > 0 && (
              <p className="text-center text-xs text-slate-500 py-6">
                No employees match "{query}"
              </p>
            )}

            {/* Results list */}
            {results.map((user) => {
              const isCreating = creating === user.employee_id;
              return (
                <button
                  key={user.employee_id}
                  onClick={() => handleSelect(user)}
                  disabled={!!creating}
                  className="
                    w-full flex items-center gap-3 px-4 py-2.5
                    hover:bg-white/5 transition-colors text-left
                    disabled:opacity-60 disabled:cursor-not-allowed
                  "
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center text-white text-xs font-bold shrink-0 overflow-hidden">
                    <UserAvatar photo={user.photo} name={user.full_name} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200 truncate font-medium">
                      {user.full_name} {user.employee_id === currentUserId && "(You)"}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {user.employee_id}
                      {user.role       ? ` · ${user.role}`       : ""}
                      {user.department ? ` · ${user.department}` : ""}
                    </p>
                  </div>

                  {/* Spinner or label */}
                  {isCreating ? (
                    <FiLoader size={14} className="text-blue-400 animate-spin shrink-0" />
                  ) : (
                    <span className="text-[10px] text-slate-600 shrink-0">Message</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-white/5">
            <p className="text-[10px] text-slate-600 text-center">
              {allUsers.length > 0
                ? `${results.length} of ${allUsers.length} employees`
                : "Press Esc to close"}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}